import { RightraClient } from "./client.js";
import { RightraPlayer, RightraPlaybackError } from "./player.js";
import { RightraError } from "./errors.js";
import {
  generateContentKey,
  exportKeyBase64,
  bytesToBase64,
  base64ToBytes,
  importAesKey,
  encryptSegmentAt,
  createKeyExchange,
  unwrapSessionKey,
} from "./crypto.js";
import { buildManifest, serializeManifest } from "./manifest.js";
import { planFragments } from "./mp4.js";
import { ensureFragmented } from "./fragmenter.js";

class RightraRoot {
  constructor(options) {
    this.client = new RightraClient(options);
  }

  /**
   * Registers a work.
   *
   * The file must be a fragmented MP4. It is split on real fragment
   * boundaries, encrypted in the browser (AES-256-GCM, one IV and one
   * authenticated segment index per segment), and uploaded one segment at a
   * time through the caller's `uploadSegments` handler. Streaming keeps peak
   * memory at one segment, so files of any size work.
   *
   * @param {Object} params
   * @param {File|Blob} params.file
   * @param {String} params.title
   * @param {String} params.type
   * @param {Function} params.uploadSegments
   *   Called once per segment:
   *     async ({ contentId, index, total, segment, iv, isInit })
   *   Must store segment i as "seg-i.enc" and return { url }.
   *   Called once at the end:
   *     async ({ contentId, isManifest: true, manifest, manifestText })
   *   Must store the manifest as "manifest.json" and return { manifestUrl }.
   * @param {Number} [params.segmentBytes] target segment size, default 4 MB
   * @param {Function} [params.onProgress] ({ stage, done, total })
   */
  async registerWork({
    file,
    title,
    type,
    description,
    creator_name,
    rights_holder_name,
    uploadSegments,
    segmentBytes = 4 * 1024 * 1024,
    mime,
    width,
    height,
    onProgress,
  }) {
    if (!file) throw new Error("A file is required.");
    if (typeof uploadSegments !== "function") {
      throw new Error("An uploadSegments handler is required.");
    }
    const progress = (stage, done = 0, total = 0) => onProgress && onProgress({ stage, done, total });

progress("analyzing");
const source = await ensureFragmented(file);
const plan = await planFragments(source, { segmentBytes });

    const { content } = await this.client.registerContent({
      title,
      type,
      description,
      creator_name,
      rights_holder_name,
      encryption: { enabled: true, algorithm: "AES-256-GCM" },
    });
    const contentId = content.content_id;

    const keyBytes = generateContentKey();
    const key = await importAesKey(keyBytes, ["encrypt"]);

    const total = plan.parts.length;
    const ivs = new Array(total);
    const sizes = new Array(total);
    const segmentUrls = new Array(total);

    for (let i = 0; i < total; i++) {
      const { encrypted, iv } = await encryptSegmentAt(source, plan.parts[i], key, contentId, i);
      ivs[i] = iv;
      sizes[i] = encrypted.byteLength;

      const result = await uploadSegments({
        contentId,
        index: i,
        total,
        segment: encrypted,
        iv,
        isInit: !!plan.parts[i].init,
      });
      segmentUrls[i] = result && result.url ? result.url : null;

      progress("uploading", i + 1, total);
    }

    const manifest = buildManifest({
      contentId,
      mime: mime || plan.mime,
      width: width || plan.width,
      height: height || plan.height,
      duration: plan.duration,
      durationEstimated: plan.durationEstimated,
      timeIndexed: plan.timeIndexed,
      parts: plan.parts,
      ivs: ivs.map((iv) => bytesToBase64(iv)),
      sizes,
    });

    progress("storing-manifest");
    const manifestResult = await uploadSegments({
      contentId,
      isManifest: true,
      total,
      manifest,
      manifestText: serializeManifest(manifest),
    });
    if (!manifestResult || !manifestResult.manifestUrl) {
      throw new Error("uploadSegments must return { manifestUrl } for the manifest.");
    }

    progress("storing-key");
    await this.client.uploadKey(contentId, {
      key_material: exportKeyBase64(keyBytes),
      key_algorithm: "AES-256-GCM",
      segment_manifest: manifest.segments.map((s) => ({ index: s.index, iv: s.iv, size: s.size })),
    });

    progress("storing-manifest-url");
    await this.client.uploadManifest(contentId, {
      manifest_url: manifestResult.manifestUrl,
      storage_reference: manifestResult.storageReference || null,
    });

    progress("done");
    return {
      content,
      manifest,
      manifestUrl: manifestResult.manifestUrl,
      storageReference: manifestResult.storageReference || null,
      segmentUrls,
    };
  }

  async createPlayer(container, { contentId, manifestUrl, viewer, device, territory, watermark, onError, onEnded }) {
    const exchange = await createKeyExchange();
    const session = await this.client.createPlaybackSession({
      content_id: contentId,
      viewer,
      device,
      requested_territory: territory,
      client_public_key: exchange.publicKeyB64,
    });

    const code = Number(session && session.watermark_code);
    if (!Number.isInteger(code) || code < 0) {
      console.error("[Rightra] session response is missing watermark_code", session);
      throw new RightraError("SESSION_NOT_PROTECTED", "This viewing session could not be protected.", 0);
    }

    let keyBytes;
    if (session.key_wrapped) {
      keyBytes = await unwrapSessionKey({
        privateKey: exchange.privateKey,
        wrapped: session.key_wrapped,
        sessionId: session.session_id,
      });
    } else if (session.session_key) {
      keyBytes = base64ToBytes(session.session_key);
    } else {
      throw new RightraError("NO_KEY", "The server did not return a playback key.", 0);
    }

    const player = new RightraPlayer(container, {
      client: this.client,
      contentId,
      manifestUrl,
      keyBytes,
      watermarkPayload: session.watermark_payload,
      sessionId: session.session_id,
      sessionCode: code,
      downloadAllowed: session.download_allowed === true,
      watermark,
      onError,
      onEnded,
    });

    try {
      await player.load();
    } catch (err) {
      await player.destroy();
      throw err;
    }
    return player;
  }
}

export function init(options) {
  return new RightraRoot(options);
}

export { RightraClient, RightraPlayer, RightraPlaybackError, RightraError };
export { buildManifest, serializeManifest, planFragments };